import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import FroalaEditor from 'react-froala-wysiwyg'
import { Box } from '@mui/material'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/plugins/colors.min.css'
import 'froala-editor/css/plugins/image.min.css'
import 'froala-editor/css/plugins/table.min.css'
import 'froala-editor/js/plugins/colors.min.js'
import 'froala-editor/js/plugins/font_size.min.js'
import 'froala-editor/js/plugins/image.min.js'
import 'froala-editor/js/plugins/link.min.js'
import 'froala-editor/js/plugins/table.min.js'

import { BASE_URL_WITH_ALL_REACT } from '~config'

import './froala.less'

interface FroalaEditorProps {
  height: number
  maxHeight: number
  initialContent: string
  onChange?: (htmlContent: string) => void
  className?: string
  isTemplateEditor?: boolean
}

export interface FroalaEditorRef {
  getContent: () => string
  setContent: (content: string) => void
}

const FroalaEditorComponent = forwardRef<FroalaEditorRef, FroalaEditorProps>(
  ({ height, maxHeight, initialContent, onChange, className, isTemplateEditor = false }, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorRef = useRef<any>(null)

    useImperativeHandle(ref, () => ({
      getContent: (): string => {
        return editorRef.current?.editor.html.get() || ''
      },
      setContent: (content: string): void => {
        if (editorRef.current?.editor) {
          editorRef.current.editor.html.set(content)
        }
      },
    }))

    const handleModelChange = (model: string): void => {
      if (onChange) {
        const htmlContent = editorRef.current?.editor.html.get() || model
        onChange(htmlContent)
      }
    }

    const config = {
      heightMin: height,
      heightMax: maxHeight,
      width: '100%',
      key: 'VCC6kE4E3E3H3C10A7D7cWHNGGDTCWHIg1Ee1Oc2Yc1b1Lg1POkB6B5F5F4F3E3G3F2B6C3==',
      imageUploadURL: 'https://app.polimorphic.com/upload',
      fileUploadURL: 'https://app.polimorphic.com/upload',
      toolbarInline: false,
      toolbarVisibleWithoutSelection: true,
      pluginsEnabled: ['link', 'colors', 'fontSize'],
      fontFamilyDefaultSelection: 'avenir-light, sans-serif',
      fontFamily: {
        "'Times New Roman', sans-serif": 'Font 1',
        'avenir-medium, sans-serif': 'Font 2',
      },
      linkAlwaysBlank: true,
      linkList: isTemplateEditor
        ? [
            {
              text: 'Case Link',
              href: `${BASE_URL_WITH_ALL_REACT}/cases/{{$CASE_ID}}/apply`,
              target: '_blank',
            },
            {
              text: 'Internal Case Link',
              href: `${BASE_URL_WITH_ALL_REACT}/cases/{{$CASE_ID}}`,
              target: '_blank',
            },
          ]
        : [],
      toolbarButtons: {
        moreText: {
          buttons: [
            'bold',
            'italic',
            'underline',
            'fontSize',
            'textColor',
            'backgroundColor',
            'clearFormatting',
          ],
          align: 'left',
          buttonsVisible: 7,
        },
        moreParagraph: {
          buttons: [
            'alignLeft',
            'alignCenter',
            'formatOLSimple',
            'formatULSimple',
            'alignRight',
            'alignJustify',
            'outdent',
            'indent',
            'quote',
          ],
          align: 'left',
          buttonsVisible: 9,
        },
        moreRich: {
          buttons: ['insertLink', 'insertImage', 'insertTable'],
          align: 'left',
          buttonsVisible: 3,
        },
        moreMisc: {
          buttons: ['undo', 'redo'],
          align: 'right',
          buttonsVisible: 2,
        },
      },
      events: {
        'image.uploaded': function (response: string): boolean {
          const imgUrl = `https://s3-us-east-2.amazonaws.com/polimorphic-sha256/${response.replace(/"/g, '')}`
          // @ts-ignore
          this.image.insert(imgUrl, false, null, this.image.get(), response)
          return false
        },
        'file.uploaded': function (response: string): boolean {
          const imgUrl = `https://s3-us-east-2.amazonaws.com/polimorphic-sha256/${response.replace(/"/g, '')}`
          // @ts-ignore
          this.file.insert(imgUrl, false, null, this.file.get(), response)
          return false
        },
        contentChanged: function (): void {
          // @ts-ignore
          handleModelChange(this.html.get())
        },
      },
    }

    return (
      <Box
        className={className}
        tabIndex={0}
        sx={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          '&:hover': {
            border: '1px solid black',
          },
          '&:focus-visible': {
            border: '2px solid #ffffff',
            outline: '2px solid #0894cf',
            boxShadow: '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
          },
        }}
      >
        <FroalaEditor
          tag='textarea'
          config={config}
          model={initialContent}
          onModelChange={handleModelChange}
          ref={editorRef}
        />
      </Box>
    )
  },
)

export default FroalaEditorComponent
